import type { Entry } from "../types";

export const askSageMessages = {
  navAskSage: { ko: "세이지에게 묻기", en: "Ask Sage" },
  navAskSageLog: { ko: "질문 기록", en: "Ask Sage log" },
  navAskSageLogPlatform: { ko: "Ask Sage 기록", en: "Ask Sage log" },

  askSageTitle: { ko: "세이지에게 묻기", en: "Ask Sage" },

  // Guideline panel
  askSageGuideTitle: { ko: "Sage가 답할 수 있는 것", en: "What Sage can answer" },
  askSageGuideCan: { ko: "답할 수 있는 질문", en: "Sage can answer" },
  askSageGuideCanExample1: {
    ko: "\"광고에 '최고'라는 표현을 써도 되나요?\" — 금지 표현과 대체 문구를 확인합니다.",
    en: "\"Can I use the word 'best' in a material?\" — checks the dictionary and suggests wording.",
  },
  askSageGuideCanExample2: {
    ko: "\"온라인 리플렛에 수익률을 표시할 때 필요한 문구가 있나요?\" — 해당 조합에 적용되는 규칙을 확인합니다.",
    en: "\"What wording is required when showing a return rate in an online leaflet?\" — checks the rules that apply to that combination.",
  },
  askSageGuideCanExample3: {
    ko: "\"반려된 검토를 어떻게 재제출하나요?\" — 시스템 사용법을 안내합니다.",
    en: "\"How do I resubmit a rejected review?\" — explains how to use the system.",
  },
  askSageGuideCannot: { ko: "답할 수 없는 질문", en: "Sage cannot answer" },
  askSageGuideCannotExample1: {
    ko: "\"이 자료가 심의를 통과할까요?\" — 자료 전체의 승인 여부는 담당자 심의 사항입니다. 검토를 요청하세요.",
    en: "\"Will this material pass review?\" — approving a material is a reviewer's decision. Submit it for review.",
  },
  askSageGuideCannotExample2: {
    ko: "등록되지 않은 기준에 대한 질문 — 근거가 없으면 Sage는 모른다고 답합니다.",
    en: "Questions about standards that are not registered — with no source, Sage says it does not know.",
  },
  askSageGuideNotApproval: {
    ko: "이 답변은 심의 결과나 법적 승인이 아닙니다. 판단이 필요하면 검토를 요청하세요.",
    en: "An answer here is not a review result or legal approval. Submit a review when a judgement is needed.",
  },
  askSageRecordedNotice: {
    ko: "질문과 답변은 저장되며, 조직의 관리자가 열람할 수 있습니다.",
    en: "Questions and answers are recorded and can be read by your organization's administrators.",
  },

  // Composer
  askSagePlaceholder: {
    ko: "질문을 입력하세요 (질문, 답변 기록됨)",
    en: "Type your question (questions and answers are recorded)",
  },
  askSageSend: { ko: "질문하기", en: "Ask" },
  // Progress wording tracks the pipeline the server actually runs, so a long
  // wait reads as work in progress rather than a stuck screen.
  askSageStageReading: { ko: "질문을 읽고 있습니다", en: "Reading your question" },
  askSageStageRetrieving: { ko: "관련 기준을 찾고 있습니다", en: "Finding the relevant standards" },
  askSageStageDocuments: { ko: "정책 문서를 확인하고 있습니다", en: "Checking the policy documents" },
  askSageStageComposing: { ko: "답변을 정리하고 있습니다", en: "Writing the answer" },
  askSageStageSlow: {
    ko: "정책 문서를 읽는 중이라 시간이 걸릴 수 있습니다.",
    en: "Reading the policy documents can take a while.",
  },
  askSageElapsed: { ko: "{seconds}초", en: "{seconds}s" },
  askSageNewConversation: { ko: "새 질문", en: "New question" },
  askSageMyConversations: { ko: "내 질문", en: "My questions" },
  askSageEmptyHeadline: { ko: "무엇을 확인할까요?", en: "What would you like to check?" },
  askSageEmptyHint: {
    ko: "자료를 제출하기 전에 표현이나 기준을 물어보세요.",
    en: "Ask about a phrase or a standard before you submit.",
  },

  // Example questions. The chip is short; clicking it writes the full question.
  askSageExample1Chip: { ko: "'최고' 표현", en: "The word \"best\"" },
  askSageExample1: {
    ko: "광고에 '최고'라는 표현을 써도 되나요?",
    en: "Can I use the word \"best\" in a material?",
  },
  askSageExample2Chip: { ko: "수익률 표시 문구", en: "Return-rate wording" },
  askSageExample2: {
    ko: "수익률을 표시할 때 반드시 함께 써야 하는 문구가 있나요?",
    en: "Is there wording I must include when showing a return rate?",
  },
  askSageExample3Chip: { ko: "재제출 방법", en: "How to resubmit" },
  askSageExample3: {
    ko: "반려된 검토를 어떻게 수정해서 다시 제출하나요?",
    en: "How do I revise a rejected review and submit it again?",
  },

  // Scope selector
  askSageScope: { ko: "범위 지정", en: "Set a scope" },
  askSageScopeHelp: {
    ko: "채널과 자료 구분을 지정하면 그 조합에 적용되는 기준만으로 답변합니다. 대화 중에는 바꿀 수 없습니다.",
    en: "Narrowing to a channel and category answers from only the standards that apply to that combination. It is fixed once the conversation starts.",
  },
  askSageScopeChannel: { ko: "채널", en: "Channel" },
  askSageScopeCategory: { ko: "자료 구분", en: "Material category" },
  askSageScopeAnyChannel: { ko: "채널 지정 없음", en: "Any channel" },
  askSageScopeAnyCategory: { ko: "자료 구분 지정 없음", en: "Any category" },
  askSageScopeFixed: { ko: "이 대화의 범위: {scope}", en: "Scope of this conversation: {scope}" },
  askSageScopeWholeOrg: { ko: "조직 전체", en: "Whole organization" },

  // Answers
  askSageBasis: { ko: "근거", en: "Based on" },
  askSageNoBasis: {
    ko: "등록된 기준에서 근거를 찾지 못했습니다.",
    en: "No registered standard covered this.",
  },
  askSageKindCOMPLIANCE: { ko: "기준 안내", en: "Standards" },
  askSageKindSYSTEM_USAGE: { ko: "사용법 안내", en: "How to use" },
  askSageKindNEEDS_REVIEW: { ko: "심의 필요", en: "Needs a review" },
  askSageKindOUT_OF_SCOPE: { ko: "답변 범위 밖", en: "Out of scope" },
  askSageKindFAILED: { ko: "답변 실패", en: "Failed" },
  askSageSubmitReview: { ko: "검토 요청하기", en: "Submit a review" },
  errAskSage: { ko: "질문을 처리하지 못했습니다.", en: "The question could not be answered." },

  // Administrator log
  askSageLogTitle: { ko: "Sage 질문 기록", en: "Ask Sage log" },
  askSageLogIntro: {
    ko: "구성원이 무엇을 묻고 Sage가 어떻게 답했는지 확인합니다. 답변 실패와 '근거 없음'이 많다면 사전이나 기준에 공백이 있다는 뜻입니다.",
    en: "See what members asked and how Sage answered. Many failures or unsourced answers mean a gap in your dictionary or standards.",
  },
  askSageLogSearch: { ko: "질문, 답변 내용 검색", en: "Search questions and answers" },
  askSageLogSessions: { ko: "대화", en: "Conversations" },
  askSageLogQuestions: { ko: "질문 수", en: "Questions" },
  askSageLogAskers: { ko: "질문한 구성원", en: "Members who asked" },
  askSageLogLastAsked: { ko: "마지막 질문", en: "Last asked" },
  askSageLogByKind: { ko: "답변 유형 분포", en: "Answers by type" },
  askSageLogTopAskers: { ko: "질문이 많은 구성원", en: "Most active askers" },
  askSageLogTopCitations: { ko: "많이 인용된 기준", en: "Most cited standards" },
  askSageLogCited: { ko: "{count}회 인용", en: "cited {count}×" },
  askSageLogSelectSession: { ko: "왼쪽에서 대화를 선택하세요.", en: "Pick a conversation on the left." },
  askSageLogEmpty: { ko: "기록이 없습니다.", en: "No conversations yet." },
  askSageLogViewAudited: {
    ko: "다른 구성원의 대화를 열면 감사 로그에 기록됩니다.",
    en: "Opening another member's conversation is recorded in the audit log.",
  },

  // Retention and rollover
  askSagePurgedTitle: { ko: "보존 기간이 지난 대화", en: "Conversation past its retention window" },
  askSagePurgedBody: {
    ko: "보존 기간이 지나 내용이 삭제되었습니다. 질문 수와 인용 통계는 유지됩니다.",
    en: "The text was removed at the end of its retention window. Question counts and citation statistics are kept.",
  },
  askSagePartialKnowledge: { ko: "기준 발췌", en: "Partial standards" },
  askSagePartialKnowledgeHelp: {
    ko: "등록된 기준이 많아 질문과 관련성이 높은 항목만으로 답했습니다. 목록에 없는 기준이 있을 수 있습니다.",
    en: "The knowledge base was too large to send whole, so this answer used the most relevant entries. A standard outside that extract may exist.",
  },
  askSageContinuedFrom: { ko: "이전 대화에서 이어짐", en: "Continued from an earlier conversation" },
  askSageRolledOverNotice: {
    ko: "이전 대화가 길어지거나 시간이 지나 새 대화로 이어졌습니다.",
    en: "The earlier conversation was long or idle, so this continues as a new one.",
  },
  askSageRetentionTitle: { ko: "Sage 질문 보존 기간", en: "Ask Sage retention" },
  askSageRetentionHelp: {
    ko: "기간이 지나면 질문과 답변 내용이 삭제됩니다. 질문 수, 답변 유형, 인용 통계는 유지되므로 사용량과 기준 공백은 계속 확인할 수 있습니다.",
    en: "Past this window the question and answer text is deleted. Question counts, answer types and citation statistics are kept, so usage and knowledge gaps stay measurable.",
  },
  askSageRetentionForever: { ko: "삭제하지 않음", en: "Keep indefinitely" },
  askSageRetentionDays: { ko: "{days}일", en: "{days} days" },
  askSageRetentionMonths: { ko: "{months}개월", en: "{months} months" },
  askSageRetentionYears: { ko: "{years}년", en: "{years} years" },
} as const satisfies Record<string, Entry>;
